/**
 * Mobile Storage Helper - Handles localStorage issues in Capacitor/Android
 * Provides fallback to Capacitor Preferences API if needed
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const STORAGE_KEY = 'omnigate_api_key';
const ADMIN_KEY_STORAGE = 'omnigate_admin_key';
const API_URL_STORAGE = 'omnigate_api_url';

// Keys that should NOT be sent as auth (placeholder/init values)
const INVALID_KEYS = new Set([
  'admin-initial-key',
  'admin-init-key',
  'nexus-admin-2026',
  '',
]);

let useCapacitorPreferences = false;

/**
 * Initialize storage - detect if we need to use Capacitor Preferences
 */
export async function initMobileStorage(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    console.log('[MobileStorage] Running in browser, using localStorage');
    return;
  }

  console.log('[MobileStorage] Native platform detected, testing localStorage...');
  
  try {
    // Test if localStorage works
    const testKey = '_test_storage_' + Date.now();
    localStorage.setItem(testKey, 'test');
    const retrieved = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    
    if (retrieved === 'test') {
      console.log('[MobileStorage] localStorage works fine!');
      useCapacitorPreferences = false;
    } else {
      console.warn('[MobileStorage] localStorage returned different value, switching to Capacitor Preferences');
      useCapacitorPreferences = true;
    }
  } catch (error) {
    console.warn('[MobileStorage] localStorage error:', error);
    console.log('[MobileStorage] Switching to Capacitor Preferences API');
    useCapacitorPreferences = true;
  }
}

/**
 * Get item from storage (auto-detects best method)
 */
export async function getItem(key: string): Promise<string | null> {
  if (!useCapacitorPreferences) {
    return localStorage.getItem(key);
  }
  
  try {
    const { value } = await Preferences.get({ key });
    console.log(`[MobileStorage] Got ${key} via Capacitor:`, value ? '***' + value.slice(-4) : 'null');
    return value;
  } catch (error) {
    console.error('[MobileStorage] Error getting preference:', error);
    return null;
  }
}

/**
 * Set item in storage (auto-detects best method)
 */
export async function setItem(key: string, value: string): Promise<void> {
  if (!useCapacitorPreferences) {
    localStorage.setItem(key, value);
    console.log(`[MobileStorage] Set ${key} via localStorage`);
    return;
  }
  
  try {
    await Preferences.set({ key, value });
    console.log(`[MobileStorage] Set ${key} via Capacitor`);
  } catch (error) {
    console.error('[MobileStorage] Error setting preference:', error);
    throw error;
  }
}

/**
 * Remove item from storage
 */
export async function removeItem(key: string): Promise<void> {
  if (!useCapacitorPreferences) {
    localStorage.removeItem(key);
    return;
  }
  
  try {
    await Preferences.remove({ key });
  } catch (error) {
    console.error('[MobileStorage] Error removing preference:', error);
  }
}

/**
 * Get API key with auto-initialization
 */
export async function getApiKeyAsync(): Promise<string> {
  const key = await getItem(STORAGE_KEY);
  return key || '';
}

/**
 * Check if API key is valid
 */
export function isValidKey(key: string): boolean {
  return key.length > 0 && !INVALID_KEYS.has(key);
}

/**
 * Build auth headers (async version for mobile)
 */
export async function authHeadersAsync(): Promise<Record<string, string>> {
  const key = await getApiKeyAsync();
  
  if (!isValidKey(key)) {
    console.warn('[authHeadersAsync] No valid API key!');
    return {};
  }
  
  console.log('[authHeadersAsync] Using API key ending in:', '***' + key.slice(-4));
  return {
    'Authorization': `Bearer ${key}`,
    'X-API-Key': key,
  };
}

// Export original sync methods for backward compatibility
export function getApiKeySync(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch (e) {
    console.warn('[getApiKeySync] localStorage error, returning empty:', e);
    return '';
  }
}
